import { useEffect, useRef, useState } from 'react';

import { useDebouncedValue } from '@tanstack/react-pacer/debouncer';
import { getRouteApi } from '@tanstack/react-router';

const route = getRouteApi('/reporting');
const SEARCH_DEBOUNCE_MS = 300;

// Local state absorbs every keystroke; only the debounced value reaches the `search` URL param.
export function useReportingSearchInput() {
    const { search } = route.useSearch();
    const navigate = route.useNavigate();
    const [value, setValue] = useState(search ?? '');
    // Tracks the value last synced with the URL — without it, a Back/Forward resync gets re-pushed
    // as a navigation, or a debounced push gets misread as Back/Forward and clobbers in-flight typing.
    const pushedRef = useRef(search ?? '');
    const syncingFromUrlRef = useRef(false);

    useEffect(() => {
        const next = search ?? '';
        if (next === pushedRef.current) return;
        pushedRef.current = next;
        syncingFromUrlRef.current = true;
        setValue(next);
    }, [search]);

    const [debouncedValue] = useDebouncedValue(value, { wait: SEARCH_DEBOUNCE_MS });

    useEffect(() => {
        // A stale debounce from before a URL-driven change must not overwrite that navigation.
        if (syncingFromUrlRef.current) return;
        if (debouncedValue === pushedRef.current) return;
        pushedRef.current = debouncedValue;
        void navigate({
            search: (prev) => ({ ...prev, search: debouncedValue || undefined }),
            replace: true,
        });
    }, [debouncedValue, navigate]);

    const onChange = (next: string) => {
        syncingFromUrlRef.current = false;
        setValue(next);
    };

    return { value, onChange };
}
